/**
 * ITER308 · Phase C — Maintenance Dashboard tab.
 *
 * Module landing rollup:
 *   • Health strip (Healthy · Needs Attention · Critical · Out of Service).
 *   • Due Today · Upcoming 7d · Overdue (worst first).
 *   • Warranty / AMC expiring ≤30 days.
 *   • Recent service activity (last 10).
 *   • Monthly maintenance cost (last 6 months, sparkline bars).
 *   • Compliance % (completed-on-time / due, last 30d).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Activity, AlertTriangle, Calendar, Clock, Wrench, RefreshCw,
  ShieldAlert, TrendingUp, ClipboardCheck,
} from 'lucide-react';

const HEALTH = {
  healthy:         { label: 'Healthy',         cls: 'text-emerald-800 bg-emerald-50 border-emerald-200' },
  needs_attention: { label: 'Needs Attention', cls: 'text-amber-800 bg-amber-50 border-amber-200' },
  critical:        { label: 'Critical',        cls: 'text-red-800 bg-red-50 border-red-200' },
  out_of_service:  { label: 'Out of Service',  cls: 'text-slate-700 bg-slate-100 border-slate-300' },
};

function HeadingBand({ title, subtitle, action }) {
  return (
    <div className="rounded-lg mb-3 overflow-hidden border border-[color:var(--color-primary)]/30">
      <div className="flex items-center justify-between px-4 py-2 gap-3"
           style={{ background: 'var(--color-primary)', color: 'white' }}>
        <div>
          <div className="text-sm font-semibold tracking-wide">{title}</div>
          {subtitle && <div className="text-[11px] opacity-80">{subtitle}</div>}
        </div>
        {action}
      </div>
    </div>
  );
}

function relative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'today';
  return `in ${days}d`;
}

const fmtInr = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;

export default function MaintenanceDashboardTab() {
  const { getHeaders, API, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [readings, setReadings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, rdRes] = await Promise.all([
        axios.get(`${API}/admin/maintenance/dashboard`, { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/daily-readings/dashboard`, { headers: getHeaders() }),
      ]);
      setData(dashRes.data);
      setReadings(rdRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await axios.post(`${API}/admin/maintenance/recompute-health`, {},
                        { headers: getHeaders() });
      toast.success('Health recomputed for all assets');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recompute failed');
    } finally { setRecomputing(false); }
  };

  if (loading || !data) {
    return <div className="text-sm text-[#5C6B64] py-8 text-center">Loading dashboard…</div>;
  }

  const isAdmin = ['admin', 'area_manager', 'super_admin'].includes(user?.role);
  const H = data.health_counts || {};
  const totalAssets = Object.values(H).reduce((a, b) => a + (b || 0), 0);
  const compliance = data.compliance || { due: 0, on_time: 0, pct: 100 };

  const monthlyMax = Math.max(
    1,
    ...(data.monthly_cost || []).map((m) => Number(m.total) || 0),
  );

  return (
    <div data-testid="maintenance-dashboard-tab">
      {/* Health strip */}
      <HeadingBand
        title={`Assets by Health · ${totalAssets} live`}
        action={
          isAdmin && (
            <Button size="sm" variant="ghost" onClick={recompute}
                     disabled={recomputing}
                     className="text-white hover:bg-white/10"
                     data-testid="recompute-health-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${recomputing ? 'animate-spin' : ''}`} />
              Recompute
            </Button>
          )
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {['healthy', 'needs_attention', 'critical', 'out_of_service'].map((k) => {
          const st = HEALTH[k];
          return (
            <Card key={k} className={`border ${st.cls} shadow-none`}
                  data-testid={`health-tile-${k}`}>
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold">
                  {st.label}
                </div>
                <div className="text-2xl font-bold mt-1">{H[k] || 0}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <TaskColumn title="Due Today" icon={Clock}
                     rows={data.due_today} testid="col-due-today"
                     onOpen={(r) => navigate(`/admin/maintenance/assets/${r.asset_id}`)} />
        <TaskColumn title="Upcoming (7 days)" icon={Calendar}
                     rows={data.upcoming_7} testid="col-upcoming"
                     onOpen={(r) => navigate(`/admin/maintenance/assets/${r.asset_id}`)} />
        <TaskColumn title="Overdue" icon={AlertTriangle} isOverdue
                     rows={data.overdue} testid="col-overdue"
                     onOpen={(r) => navigate(`/admin/maintenance/assets/${r.asset_id}`)} />
      </div>

      {/* ITER311 · Daily Readings signals — compliance KPI, out-of-range, recent alerts */}
      {readings && (
        <>
          <HeadingBand
            title="Daily Readings Compliance"
            subtitle={`Today · ${readings.compliance?.date}`}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <Card className={`border shadow-none ${
              (readings.compliance?.total_areas || 0) === (readings.compliance?.submitted_areas || 0)
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`} data-testid="readings-compliance-kpi">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" />
                  <span className="text-[10px] uppercase font-semibold tracking-wider">
                    Areas submitted today
                  </span>
                </div>
                <div className="text-2xl font-bold mt-1"
                      data-testid="readings-compliance-count">
                  {readings.compliance?.submitted_areas || 0}/{readings.compliance?.total_areas || 0}
                </div>
                <div className="text-[10.5px] mt-0.5 opacity-70">
                  {readings.compliance?.submitted || 0}/{readings.compliance?.due || 0} outlet(s) fully complete
                </div>
                {/* ITER311.4 · Per-outlet drill-down with area granularity. */}
                <div className="mt-2 space-y-1 text-[11px]"
                      data-testid="readings-per-outlet">
                  {(readings.compliance?.per_outlet || []).map(o => (
                    <div key={o.outlet_id} className="flex items-center justify-between gap-2">
                      <span className="truncate">{o.outlet_name}</span>
                      <span className={`shrink-0 font-semibold ${
                        o.fully_submitted ? 'text-emerald-700'
                        : o.submitted > 0 ? 'text-amber-800'
                        : 'text-red-700'
                      }`}>{o.submitted}/{o.total}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50 shadow-none md:col-span-2"
                  data-testid="readings-oor-card">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-[10px] uppercase font-semibold tracking-wider">
                    Out-of-Range Today · {readings.out_of_range?.length || 0}
                  </span>
                </div>
                {(readings.out_of_range || []).length === 0 ? (
                  <div className="text-[11px] text-amber-800/70 mt-1">All readings within expected range.</div>
                ) : (
                  <div className="mt-2 space-y-1">
                    {readings.out_of_range.slice(0, 5).map((r, i) => (
                      <div key={i} className="text-[12px] text-amber-900 flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{r.outlet_name}</span>
                        <span className="text-amber-700">·</span>
                        <span>{r.equipment}</span>
                        <span className="text-amber-700">·</span>
                        <span className="font-mono font-semibold">{r.value} {r.unit}</span>
                        <span className="text-amber-700 text-[10px]">
                          (expected {r.min ?? '—'}–{r.max ?? '—'})
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          {(readings.recent_alerts || []).length > 0 && (
            <>
              <HeadingBand title={`Recent Condition Alerts · ${readings.recent_alerts.length}`}
                            subtitle="Breakdown & maintenance-due readings, latest first" />
              <div className="space-y-2 mb-6" data-testid="readings-recent-alerts">
                {readings.recent_alerts.map((a, i) => (
                  <Card key={i} className="border-[#E2E8DF] shadow-sm">
                    <CardContent className="py-2.5 px-4 flex items-center gap-3">
                      <span className="text-[13px]"
                             style={{ color: { green:'#166534', amber:'#B45309', red:'#B91C1C' }[a.color] || '#4B5563' }}>
                        ●
                      </span>
                      <span className="text-sm font-medium text-[#1A2421]">{a.condition}</span>
                      <span className="text-xs text-[#5C6B64]">·</span>
                      <span className="text-sm text-[#1A2421]">{a.outlet_name}</span>
                      <span className="text-xs text-[#5C6B64]">·</span>
                      <span className="text-sm text-[#1A2421] flex-1 truncate">{a.equipment}</span>
                      <span className="text-[11px] text-[#5C6B64]">{a.date}</span>
                      {(a.tickets || []).length > 0 && (
                        <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-300">
                          {a.tickets.filter(Boolean).slice(0,2).join(', ')}
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Expiring warranty / AMC */}
      <HeadingBand title={`Warranty & AMC expiring · ${data.expiring?.length || 0}`}
                    subtitle="Within 30 days" />
      {(!data.expiring || data.expiring.length === 0) ? (
        <EmptyRow icon={ShieldAlert}
                  msg="Nothing expiring soon. Warranty and AMC dates all clear for 30+ days." />
      ) : (
        <Card className="border-[#E2E8DF] shadow-sm mb-6" data-testid="expiring-list">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F8]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B64]">
                  <th className="px-4 py-2.5">Asset</th>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Kind</th>
                  <th className="px-4 py-2.5">Expires</th>
                </tr>
              </thead>
              <tbody>
                {data.expiring.map((e, i) => (
                  <tr key={`${e.asset_id}-${e.kind}`}
                       className={i > 0 ? 'border-t border-[#E2E8DF]' : ''}>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-[color:var(--color-primary)] font-bold cursor-pointer"
                        onClick={() => navigate(`/admin/maintenance/assets/${e.asset_id}`)}>
                      {e.asset_tag}
                    </td>
                    <td className="px-4 py-2.5">{e.name}</td>
                    <td className="px-4 py-2.5">
                      <Badge className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">{e.kind}</Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      {e.expires_on?.slice(0, 10)}
                      <span className="text-[11px] text-[#5C6B64] ml-2">
                        {e.days < 0 ? `${Math.abs(e.days)}d overdue` : `in ${e.days}d`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Compliance + Monthly cost */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <HeadingBand title="Compliance (30 days)" subtitle="Completed on-time / due" />
          <Card className="border-[#E2E8DF] shadow-sm" data-testid="compliance-card">
            <CardContent className="py-5 px-5">
              <div className="flex items-baseline gap-3">
                <div className={`text-4xl font-bold ${
                  compliance.pct >= 90 ? 'text-emerald-700'
                  : compliance.pct >= 70 ? 'text-amber-700'
                  : 'text-red-700'
                }`}>
                  {compliance.pct}%
                </div>
                <div className="text-sm text-[#5C6B64]">
                  {compliance.on_time} on-time of {compliance.due} due
                </div>
              </div>
              <div className="mt-3 h-2 bg-[#E2E8DF] rounded-full overflow-hidden">
                <div className={`h-full ${
                  compliance.pct >= 90 ? 'bg-emerald-600'
                  : compliance.pct >= 70 ? 'bg-amber-500'
                  : 'bg-red-600'
                }`} style={{ width: `${Math.min(100, compliance.pct)}%` }} />
              </div>
              <div className="text-[11px] text-[#5C6B64] mt-3">
                Target ≥ 90%. Below 70% indicates a maintenance backlog risk.
              </div>
            </CardContent>
          </Card>
        </div>
        <div>
          <HeadingBand title="Maintenance cost · Last 6 months" subtitle="From verified service history" />
          <Card className="border-[#E2E8DF] shadow-sm" data-testid="monthly-cost-card">
            <CardContent className="py-4 px-5">
              <div className="flex items-end gap-2 h-24">
                {(data.monthly_cost || []).map((m) => {
                  const h = Math.round((Number(m.total) / monthlyMax) * 100);
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center justify-end">
                      <div className="text-[10px] text-[#5C6B64] mb-1">{fmtInr(m.total)}</div>
                      <div
                        className={`w-full rounded-t transition-all ${
                          Number(m.total) > 0 ? 'bg-[color:var(--color-primary)]' : 'bg-[#E2E8DF]'
                        }`}
                        style={{ height: `${Math.max(3, h)}%` }}
                        title={`${m.month} · ${fmtInr(m.total)}`}
                      />
                      <div className="text-[10px] text-[#5C6B64] mt-1">{m.month}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent service activity */}
      <HeadingBand title={`Recent service activity · ${data.recent_service?.length || 0}`}
                    subtitle="Newest first" />
      {(!data.recent_service || data.recent_service.length === 0) ? (
        <EmptyRow icon={Wrench}
                  msg="No service history yet. Verified PM tasks and manually-logged entries land here." />
      ) : (
        <div className="space-y-2" data-testid="recent-service-list">
          {data.recent_service.map((r, i) => (
            <Card key={i} className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-2.5 px-4 flex items-center gap-3">
                <Badge className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
                  {r.label}
                </Badge>
                <span className="text-[12px] text-[#5C6B64]">
                  {r.performed_on} · {relative(r.performed_on)}
                </span>
                <span className="text-sm text-[#1A2421] flex-1 truncate">
                  {r.remarks || '—'}
                </span>
                <span className="text-[11px] text-[#5C6B64]">
                  {r.technician || '—'}
                </span>
                <span className="text-[12px] text-[#1A2421] font-semibold w-20 text-right">
                  {r.cost > 0 ? fmtInr(r.cost) : '—'}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskColumn({ title, icon: Icon, rows, isOverdue, testid, onOpen }) {
  const count = rows?.length || 0;
  return (
    <div data-testid={testid}>
      <div className={`rounded-lg mb-2 overflow-hidden border ${
        isOverdue ? 'border-red-300' : 'border-[color:var(--color-primary)]/30'
      }`}>
        <div className={`flex items-center justify-between px-3 py-2 gap-2 ${
          isOverdue ? 'bg-red-700' : 'bg-[color:var(--color-primary)]'
        } text-white`}>
          <div className="flex items-center gap-2">
            <Icon className="w-3.5 h-3.5" />
            <span className="text-xs font-semibold tracking-wide">{title}</span>
          </div>
          <span className="text-xs font-mono">{count}</span>
        </div>
      </div>
      {count === 0 ? (
        <Card className="border-dashed border-[#E2E8DF] shadow-none">
          <CardContent className="py-6 text-center text-xs text-[#5C6B64]">
            None.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {rows.slice(0, 8).map((r) => (
            <Card key={r.id} className="border-[#E2E8DF] shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => onOpen?.(r)}>
              <CardContent className="py-2 px-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] text-[#1A2421] truncate">
                    {r.description}
                  </div>
                  <span className={`text-[10px] font-mono ${
                    r.days < 0 ? 'text-red-700 font-bold' : 'text-[#5C6B64]'
                  }`}>
                    {r.days < 0 ? `${Math.abs(r.days)}d overdue`
                     : r.days === 0 ? 'today'
                     : `in ${r.days}d`}
                  </span>
                </div>
                <div className="text-[10px] text-[#5C6B64] mt-0.5 font-mono">
                  {r.due_date}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyRow({ icon: Icon, msg }) {
  return (
    <Card className="border-dashed border-[#E2E8DF] shadow-none mb-6">
      <CardContent className="py-8 text-center">
        <Icon className="w-6 h-6 text-[#5C6B64] mx-auto mb-2 opacity-60" />
        <div className="text-xs text-[#5C6B64] max-w-md mx-auto">{msg}</div>
      </CardContent>
    </Card>
  );
}
